/**
 * `<CorpusHealthSurface>` — the Corpus Health surface root (frontend.md §1.3 Tier-1 full
 * build, §2.1).
 *
 * "Pool Health" is the observability window into whether the backend extraction pipeline is
 * working. Composes the extraction-pipeline panels:
 *   - `<ExtractionQueuePanel>`     — D-006 per-stage queue
 *   - `<FailedExtractionTriage>`   — retry-exhausted jobs (no silent drops)
 *
 * History (relevant to the Trash bouncing around):
 *   - 2026-05-21: trimmed to the extraction-pipeline panels. Removed conflicted-notes
 *     (D-007), soft-deleted-notes (D-008), and the five count-report panels.
 *   - 2026-05-29 (#88): added `<SoftDeletedNotesPanel>` back here so the editor's Delete
 *     action had a recovery surface.
 *   - 2026-05-29 (#90, same day): UX feedback "Delete shouldn't live under Settings — it
 *     should be another folder in the notebooks pane with a trash icon." Moved Trash to
 *     `<TrashSection>` inside `<NotebooksTreePane>` and deleted SoftDeletedNotesPanel.
 *     Corpus Health is back to pipeline-only.
 *
 * POLLING (frontend.md §1.5): every panel polls on the capability-tiered interval and is
 * passed `active` = "this surface is the visible route" so polling pauses when the user
 * navigates away. `active` is derived from the route here and threaded down.
 *
 * DEEP LINKS (frontend.md §2.3): `?panel=…` scrolls to a panel — handled by the `anchorId`s
 * the panels carry and a scroll-into-view effect.
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { surfaceForPath } from '@/routes';
import { SurfaceLayout } from '@/components';
import { ExtractionQueuePanel } from './ExtractionQueuePanel';
import { FailedExtractionTriage } from './FailedExtractionTriage';

interface CorpusHealthSurfaceProps {
  /**
   * When `true`, this surface is rendered EMBEDDED inside another surface (the
   * `<SettingsSurface>` "Health" sub-tab post-IA-contraction 2026-05-16) — skip the
   * outer `<SurfaceLayout>` chrome so we don't render a second header inside the host.
   */
  embedded?: boolean;
  /**
   * Override the route-derived "this is the active route" flag — required when embedded
   * (the route is `/settings`, not `/health`, but the user *is* looking at the panels).
   */
  active?: boolean;
}

export function CorpusHealthSurface({
  embedded = false,
  active: activeOverride,
}: CorpusHealthSurfaceProps = {}): JSX.Element {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Polling pauses when this is not the active route (frontend.md §1.5). The override is
  // for the embedded case where the route is `/settings` — the parent passes `active`
  // based on its sub-tab state.
  const active = activeOverride ?? surfaceForPath(location.pathname) === 'health';

  // `?panel=` deep-link — scroll the named panel into view (frontend.md §2.3).
  const panelAnchor = searchParams.get('panel');
  useEffect(() => {
    if (panelAnchor) {
      const el = document.getElementById(panelAnchor);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [panelAnchor]);

  // 2026-05-21: trimmed to the extraction-pipeline panels. The Trash briefly lived here
  // (#88) and was moved to <TrashSection> inside <NotebooksTreePane> the same day (#90).
  const body = (
    <>
      <ExtractionQueuePanel active={active} />
      <FailedExtractionTriage active={active} />
    </>
  );

  if (embedded) return <div className="surface-embedded">{body}</div>;
  return <SurfaceLayout title="Pool Health">{body}</SurfaceLayout>;
}
